import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Receipt, Loader2, Eye, RotateCcw, FileSpreadsheet, Download, Plus,
  Filter, Search, CreditCard, Banknote, Building2, Send, AlertTriangle, Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const typeLabels = { normal: 'Normală', advance: 'Avans', final: 'Finală', storno: 'Storno' };
const typeColors = { normal: 'bg-blue-50 text-blue-700 border-blue-200', advance: 'bg-amber-50 text-amber-700 border-amber-200', final: 'bg-emerald-50 text-emerald-700 border-emerald-200', storno: 'bg-red-50 text-red-700 border-red-200' };
const emissionLabels = { draft: 'Draft', emitted: 'Emisă', fully_stornoed: 'Stornată total', partially_stornoed: 'Stornată parțial' };
const emissionColors = { draft: 'bg-slate-100 text-slate-600', emitted: 'bg-green-50 text-green-700', fully_stornoed: 'bg-red-50 text-red-600', partially_stornoed: 'bg-orange-50 text-orange-600' };
const paymentLabels = { unpaid: 'Neplătită', partially_paid: 'Parțial', paid: 'Plătită', cancelled: 'Anulată' };
const paymentColors = { unpaid: 'bg-amber-50 text-amber-700', partially_paid: 'bg-blue-50 text-blue-700', paid: 'bg-emerald-50 text-emerald-700', cancelled: 'bg-slate-100 text-slate-500' };
const spvLabels = { not_ready: 'Nepregătit', ready: 'Pregătit', submitted: 'Trimis', accepted: 'Acceptat', rejected: 'Respins' };
const spvColors = { not_ready: 'bg-slate-100 text-slate-500', ready: 'bg-blue-50 text-blue-600', submitted: 'bg-indigo-50 text-indigo-600', accepted: 'bg-green-50 text-green-600', rejected: 'bg-red-50 text-red-600' };

const getToken = () => localStorage.getItem('admin_token');
const authHeaders = () => ({ headers: { Authorization: 'Bearer ' + getToken() }, withCredentials: true });

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ invoice_type: 'all', payment_status: 'all', emission_status: 'all', spv_status: 'all' });
  const [searchTerm, setSearchTerm] = useState('');

  // Report
  const [reportOpen, setReportOpen] = useState(false);
  const [reportStart, setReportStart] = useState('');
  const [reportEnd, setReportEnd] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  // New invoice dialog
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [defaultVatRate, setDefaultVatRate] = useState(19);
  const [newInvoice, setNewInvoice] = useState({
    order_id: '', client_id: '', invoice_type: 'normal', is_draft: false,
    lines: [], notes: '', due_date: '', exchange_rate_margin: 1, vat_rate: 19
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => { fetchInvoices(); }, [filters]);

  const fetchInvoices = async () => {
    try {
      const params = {};
      if (filters.invoice_type !== 'all') params.invoice_type = filters.invoice_type;
      if (filters.payment_status !== 'all') params.payment_status = filters.payment_status;
      if (filters.emission_status !== 'all') params.emission_status = filters.emission_status;
      if (filters.spv_status !== 'all') params.spv_status = filters.spv_status;
      const res = await axios.get(API + '/admin/billing/invoices', { ...authHeaders(), params });
      setInvoices(res.data);
    } catch { toast.error('Eroare la încărcarea facturilor'); }
    finally { setLoading(false); }
  };

  const deleteInvoice = async (invoiceId, invoiceNumber) => {
    if (!confirm(`Sigur doriți să ștergeți factura "${invoiceNumber || 'Draft'}"?`)) return;
    try {
      await axios.delete(API + '/admin/billing/invoices/' + invoiceId, authHeaders());
      toast.success('Factură ștearsă');
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la ștergere'); }
  };

  const openNewInvoice = async () => {
    try {
      const [ordRes, cliRes, srvRes, rateRes, settingsRes] = await Promise.all([
        axios.get(API + '/admin/orders', authHeaders()),
        axios.get(API + '/admin/clients', authHeaders()),
        axios.get(API + '/services', authHeaders()),
        axios.get(API + '/exchange-rate', authHeaders()),
        axios.get(API + '/admin/billing/settings', authHeaders()),
      ]);
      setOrders(ordRes.data);
      setClients(cliRes.data);
      setServices(srvRes.data.filter(s => s.is_active));
      setExchangeRate(rateRes.data.eur_ron_rate_with_margin);
      const vatRate = settingsRes.data.vat_payer ? settingsRes.data.default_vat_rate : 0;
      setDefaultVatRate(vatRate);
      setNewInvoice({ order_id: '', client_id: '', invoice_type: 'normal', is_draft: false, lines: [], notes: '', due_date: '', exchange_rate_margin: 1, vat_rate: vatRate });
      setNewInvoiceOpen(true);
    } catch { toast.error('Eroare la încărcare date'); }
  };

  const addLine = () => {
    setNewInvoice(prev => ({
      ...prev,
      lines: [...prev.lines, { description: '', quantity: 1, unit_price_ron: 0, service_id: '' }]
    }));
  };

  const updateLine = (idx, field, value) => {
    setNewInvoice(prev => {
      const lines = [...prev.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...prev, lines };
    });
  };

  const removeLine = (idx) => {
    setNewInvoice(prev => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));
  };

  const selectServiceForLine = (idx, serviceId) => {
    const srv = services.find(s => s.service_id === serviceId);
    if (!srv) return;
    const priceRon = exchangeRate ? Math.round(srv.price * exchangeRate * 100) / 100 : 0;
    setNewInvoice(prev => {
      const lines = [...prev.lines];
      lines[idx] = { ...lines[idx], service_id: serviceId, description: srv.name, unit_price_ron: priceRon, unit_price_eur_info: srv.price };
      return { ...prev, lines };
    });
  };

  const populateFromOrder = (orderId) => {
    const order = orders.find(o => o.order_id === orderId);
    if (!order) return;
    setNewInvoice(prev => ({ ...prev, order_id: orderId, client_id: order.client_id }));
    const lines = [];
    for (const veh of (order.vehicles || [])) {
      for (const srvId of (veh.services || [])) {
        const srv = services.find(s => s.service_id === srvId);
        const priceRon = srv && exchangeRate ? Math.round(srv.price * exchangeRate * 100) / 100 : 0;
        lines.push({
          service_id: srvId,
          description: srv ? srv.name : srvId,
          quantity: 1,
          unit_price_ron: priceRon,
          unit_price_eur_info: srv ? srv.price : 0
        });
      }
    }
    setNewInvoice(prev => ({ ...prev, lines }));
  };

  const createInvoice = async () => {
    if (!newInvoice.lines.length) { toast.error('Adăugați cel puțin o linie'); return; }
    if (!newInvoice.client_id) { toast.error('Selectați un client'); return; }
    setCreating(true);
    try {
      await axios.post(API + '/admin/billing/invoices', {
        ...newInvoice,
        exchange_rate_margin: Number(newInvoice.exchange_rate_margin),
        lines: newInvoice.lines.map(l => ({ ...l, unit_price_ron: Number(l.unit_price_ron), quantity: Number(l.quantity) }))
      }, authHeaders());
      toast.success('Factură creată cu succes');
      setNewInvoiceOpen(false);
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare la creare'); }
    finally { setCreating(false); }
  };

  const generateReport = async () => {
    setLoadingReport(true);
    try {
      const params = {};
      if (reportStart) params.start_date = reportStart;
      if (reportEnd) params.end_date = reportEnd;
      const res = await axios.get(API + '/admin/billing/report', { ...authHeaders(), params });
      setReportData(res.data);
    } catch { toast.error('Eroare la raport'); }
    finally { setLoadingReport(false); }
  };

  const exportCSV = () => {
    if (!reportData) return;
    const rows = [['Metric', 'Value'],
      ['Total facturat RON', reportData.total_invoiced_ron],
      ['Total încasat RON', reportData.total_collected_ron],
      ['Total neîncasat RON', reportData.total_uncollected_ron],
      ['Cash', reportData.cash_total], ['Card', reportData.card_total], ['OP', reportData.bank_total],
      ['Facturi avans', reportData.advance_invoices], ['Facturi storno', reportData.storno_invoices],
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `Raport_${reportStart || 'all'}_${reportEnd || 'all'}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = invoices.filter(inv => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (inv.invoice_number || '').toLowerCase().includes(s) || (inv.client_name || '').toLowerCase().includes(s);
  });

  return (
    <div className="space-y-5" data-testid="admin-invoices-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Facturare</h1>
          <p className="text-sm text-slate-500">Gestionare completă facturi, plăți și SPV</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setReportData(null); setReportOpen(true); }} variant="outline" className="gap-2" data-testid="report-btn">
            <FileSpreadsheet className="h-4 w-4" />Raport
          </Button>
          <Link to="/admin/billing-settings">
            <Button variant="outline" className="gap-2" data-testid="billing-settings-btn">
              <Building2 className="h-4 w-4" />Setări facturare
            </Button>
          </Link>
          <Button onClick={openNewInvoice} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="new-invoice-btn">
            <Plus className="h-4 w-4" />Factură nouă
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Caută nr. factură sau client..." className="pl-9" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="search-invoices" />
        </div>
        <Select value={filters.invoice_type} onValueChange={v => setFilters(p => ({ ...p, invoice_type: v }))}>
          <SelectTrigger className="w-[140px]" data-testid="filter-type"><SelectValue placeholder="Tip" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate tipurile</SelectItem>
            {Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.emission_status} onValueChange={v => setFilters(p => ({ ...p, emission_status: v }))}>
          <SelectTrigger className="w-[150px]" data-testid="filter-emission"><SelectValue placeholder="Emisiune" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate stările</SelectItem>
            {Object.entries(emissionLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.payment_status} onValueChange={v => setFilters(p => ({ ...p, payment_status: v }))}>
          <SelectTrigger className="w-[140px]" data-testid="filter-payment"><SelectValue placeholder="Plată" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate plățile</SelectItem>
            {Object.entries(paymentLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.spv_status} onValueChange={v => setFilters(p => ({ ...p, spv_status: v }))}>
          <SelectTrigger className="w-[140px]" data-testid="filter-spv"><SelectValue placeholder="SPV" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate SPV</SelectItem>
            {Object.entries(spvLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><Receipt className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Nicio factură găsită</p></CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="invoices-table">
                <thead>
                  <tr className="border-b bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                    <th className="text-left px-4 py-3 font-medium">Nr. Factură</th>
                    <th className="text-left px-4 py-3 font-medium">Client</th>
                    <th className="text-left px-4 py-3 font-medium">Tip</th>
                    <th className="text-left px-4 py-3 font-medium">Data</th>
                    <th className="text-right px-4 py-3 font-medium">Total RON</th>
                    <th className="text-center px-4 py-3 font-medium">Emisiune</th>
                    <th className="text-center px-4 py-3 font-medium">Plată</th>
                    <th className="text-center px-4 py-3 font-medium">SPV</th>
                    <th className="text-center px-4 py-3 font-medium">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(inv => (
                    <tr key={inv.invoice_id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`invoice-row-${inv.invoice_id}`}>
                      <td className="px-4 py-3">
                        <span className="font-medium text-sm">{inv.invoice_number || <span className="italic text-slate-400">Draft</span>}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{inv.client_name || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={`text-xs ${typeColors[inv.invoice_type] || ''}`}>{typeLabels[inv.invoice_type] || inv.invoice_type}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">{inv.issue_date}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">
                        <span className={inv.is_storno ? 'text-red-600' : ''}>{inv.total_ron?.toFixed(2)} RON</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={`text-xs ${emissionColors[inv.emission_status] || ''}`}>{emissionLabels[inv.emission_status] || inv.emission_status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={`text-xs ${paymentColors[inv.payment_status] || ''}`}>{paymentLabels[inv.payment_status] || inv.payment_status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={`text-xs ${spvColors[inv.spv_status] || ''}`}>{spvLabels[inv.spv_status] || inv.spv_status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Link to={`/admin/invoices/${inv.invoice_id}`}>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`view-invoice-${inv.invoice_id}`}><Eye className="h-4 w-4" /></Button>
                          </Link>
                          {!['submitted', 'accepted'].includes(inv.spv_status) && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700" onClick={() => deleteInvoice(inv.invoice_id, inv.invoice_number)} data-testid={`delete-invoice-${inv.invoice_id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* New Invoice Dialog */}
      <Dialog open={newInvoiceOpen} onOpenChange={setNewInvoiceOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Factură nouă</DialogTitle>
            <DialogDescription>Creați o factură normală, avans sau finală</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tip factură</Label>
                <Select value={newInvoice.invoice_type} onValueChange={v => setNewInvoice(p => ({ ...p, invoice_type: v }))}>
                  <SelectTrigger data-testid="new-invoice-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normală</SelectItem>
                    <SelectItem value="advance">Avans</SelectItem>
                    <SelectItem value="final">Finală</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Comandă (opțional)</Label>
                <Select value={newInvoice.order_id} onValueChange={v => populateFromOrder(v)}>
                  <SelectTrigger data-testid="new-invoice-order"><SelectValue placeholder="Selectează comanda" /></SelectTrigger>
                  <SelectContent>
                    {orders.map(o => <SelectItem key={o.order_id} value={o.order_id}>{o.order_number} - {o.client_name || o.client_id}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Client *</Label>
                <Select value={newInvoice.client_id} onValueChange={v => setNewInvoice(p => ({ ...p, client_id: v }))}>
                  <SelectTrigger data-testid="new-invoice-client"><SelectValue placeholder="Selectează client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name} ({c.cnp_cui})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Scadență</Label>
                <Input type="date" value={newInvoice.due_date} onChange={e => setNewInvoice(p => ({ ...p, due_date: e.target.value }))} data-testid="new-invoice-due-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Marjă curs (% peste BNR)</Label>
                <Input type="number" step="0.1" value={newInvoice.exchange_rate_margin} onChange={e => setNewInvoice(p => ({ ...p, exchange_rate_margin: e.target.value }))} data-testid="new-invoice-margin" />
              </div>
              <div>
                <Label>TVA (%)</Label>
                <Input type="number" step="1" value={newInvoice.vat_rate} onChange={e => setNewInvoice(p => ({ ...p, vat_rate: e.target.value }))} data-testid="new-invoice-vat" />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={newInvoice.is_draft} onChange={e => setNewInvoice(p => ({ ...p, is_draft: e.target.checked }))} className="rounded border-slate-300" />
                <span className="text-sm">Salvează ca Draft</span>
              </label>
            </div>

            {/* Lines */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">Linii factură</Label>
                <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn"><Plus className="h-3 w-3 mr-1" />Linie</Button>
              </div>
              {newInvoice.lines.map((line, idx) => (
                <div key={idx} className="flex items-end gap-2 mb-2 p-3 bg-slate-50 rounded-lg" data-testid={`invoice-line-${idx}`}>
                  <div className="flex-1">
                    <Label className="text-xs">Serviciu</Label>
                    <Select value={line.service_id || ''} onValueChange={v => selectServiceForLine(idx, v)}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Alege serviciu" /></SelectTrigger>
                      <SelectContent>
                        {services.map(s => <SelectItem key={s.service_id} value={s.service_id}>{s.name} ({s.price} EUR)</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-[200px]">
                    <Label className="text-xs">Descriere</Label>
                    <Input className="h-9" value={line.description} onChange={e => updateLine(idx, 'description', e.target.value)} />
                  </div>
                  <div className="w-[70px]">
                    <Label className="text-xs">Cant.</Label>
                    <Input className="h-9" type="number" min="1" value={line.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} />
                  </div>
                  <div className="w-[110px]">
                    <Label className="text-xs">Preț RON</Label>
                    <Input className="h-9" type="number" step="0.01" value={line.unit_price_ron} onChange={e => updateLine(idx, 'unit_price_ron', e.target.value)} />
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-9 w-9 p-0 text-red-500" onClick={() => removeLine(idx)}>×</Button>
                </div>
              ))}
              {newInvoice.lines.length > 0 && (() => {
                const subtotal = newInvoice.lines.reduce((s, l) => s + (Number(l.unit_price_ron) || 0) * (Number(l.quantity) || 0), 0);
                const vatRate = Number(newInvoice.vat_rate) || 0;
                const vat = subtotal * vatRate / 100;
                const total = subtotal + vat;
                return (
                  <div className="text-right text-sm mt-2 space-y-0.5">
                    <div className="text-slate-500">Subtotal: {subtotal.toFixed(2)} RON</div>
                    {vatRate > 0 && <div className="text-slate-500">TVA ({vatRate}%): {vat.toFixed(2)} RON</div>}
                    <div className="font-bold text-slate-800">Total: {total.toFixed(2)} RON</div>
                    {exchangeRate > 0 && <div className="text-xs text-slate-400">(~{(total / exchangeRate).toFixed(2)} EUR informativ)</div>}
                  </div>
                );
              })()}
            </div>

            <div>
              <Label>Notițe</Label>
              <Textarea value={newInvoice.notes} onChange={e => setNewInvoice(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="new-invoice-notes" />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNewInvoiceOpen(false)}>Anulează</Button>
              <Button onClick={createInvoice} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-invoice-submit">
                {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {newInvoice.is_draft ? 'Salvează Draft' : 'Emite Factură'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Raport financiar</DialogTitle>
            <DialogDescription>Situație detaliată facturare și încasări</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-end gap-3">
              <div><Label>De la</Label><Input type="date" value={reportStart} onChange={e => setReportStart(e.target.value)} data-testid="report-start" /></div>
              <div><Label>Până la</Label><Input type="date" value={reportEnd} onChange={e => setReportEnd(e.target.value)} data-testid="report-end" /></div>
              <Button onClick={generateReport} disabled={loadingReport} className="bg-blue-600 text-white" data-testid="generate-report-btn">
                {loadingReport ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}Generează
              </Button>
              {reportData && <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn"><Download className="h-4 w-4 mr-1" />CSV</Button>}
            </div>
            {reportData && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-blue-600">{reportData.total_invoiced_ron?.toFixed(2)}</p><p className="text-xs text-slate-500">Facturat RON</p></CardContent></Card>
                  <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-600">{reportData.total_collected_ron?.toFixed(2)}</p><p className="text-xs text-slate-500">Încasat RON</p></CardContent></Card>
                  <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-amber-600">{reportData.total_uncollected_ron?.toFixed(2)}</p><p className="text-xs text-slate-500">Neîncasat RON</p></CardContent></Card>
                  <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-red-600">{reportData.storno_total_ron?.toFixed(2)}</p><p className="text-xs text-slate-500">Storno RON</p></CardContent></Card>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Card><CardContent className="p-3 flex items-center gap-3"><Banknote className="h-5 w-5 text-green-600" /><div><p className="text-lg font-bold">{reportData.cash_total?.toFixed(2)}</p><p className="text-xs text-slate-500">Cash</p></div></CardContent></Card>
                  <Card><CardContent className="p-3 flex items-center gap-3"><CreditCard className="h-5 w-5 text-blue-600" /><div><p className="text-lg font-bold">{reportData.card_total?.toFixed(2)}</p><p className="text-xs text-slate-500">Card</p></div></CardContent></Card>
                  <Card><CardContent className="p-3 flex items-center gap-3"><Building2 className="h-5 w-5 text-purple-600" /><div><p className="text-lg font-bold">{reportData.bank_total?.toFixed(2)}</p><p className="text-xs text-slate-500">OP / Transfer</p></div></CardContent></Card>
                </div>
                {reportData.clients_unpaid?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Clienți cu restanțe</CardTitle></CardHeader>
                    <CardContent>
                      {reportData.clients_unpaid.map((c, i) => (
                        <div key={i} className="flex justify-between py-1 border-b last:border-0">
                          <span className="text-sm">{c.name}</span>
                          <span className="text-sm font-semibold text-red-600">{c.amount?.toFixed(2)} RON</span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
